/**
 * The first visit's product events (Sol analytics + round12 E5/E6; Sol onboarding-leftovers L5 A): Mixpanel, lowercase
 * snake_case completed facts, opaque ids only - never a name, email, grade, answer text, voice or token. One visit is
 * one Mixpanel identity (distinct_id = flow_session_id); the portal hands it over in the launch link (`flow`) and the
 * player hands it back on "Explore more worlds", so the funnel does not split between the two sites.
 *
 * Nothing is sent unless a separate DEV project token is configured (WEB_MIXPANEL_TOKEN with WEB_MIXPANEL_ENV 'dev'; never the mobile MIXPANEL_TOKEN); without
 * it every call is a no-op. Server-truth events (sign-up, badges, slides, quiz answers) belong to the backend.
 */
import { WEB_MIXPANEL_ENV as MIXPANEL_ENV, WEB_MIXPANEL_TOKEN as MIXPANEL_TOKEN } from 'config/app.config';

export const PLAYER_EVENTS = new Set([
  'onboarding_lab_loaded',
  'tour_step_viewed',
  'tour_step_completed',
  'tour_skipped',
  'explore_more_clicked',
  'quikbase_arrived',
  'quikbase_control_used',
  'portal_entered',
  'unit_selector_viewed',
  'unit_selected',
  'teleport_started',
  'teleport_completed',
]);
// Any key naming a person, contact, what they said or wrote, the grade or a token is refused; has_* flags are fine.
const FORBIDDEN = /^(?!has_).*(name|email|answer_text|transcript|audio|idfa|gaid|grade|token)/i;
const FLOW_KEY = 'ql-flow-v1';
const ON = Boolean(MIXPANEL_TOKEN) && ['dev', 'prototype'].includes(MIXPANEL_ENV);

const uuid = () => {
  try {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  } catch (_) {
    return Math.random().toString(16).slice(2, 18);
  }
};
const fresh = () => ({ id: `f_${uuid()}`, seq: 0, parent: null, child: null });

/** `f_…~p_…~c_…~12` - the visit's id, its two opaque person ids and how many events it has had. */
export function formatFlow(flow) {
  return [flow.id, flow.parent || '', flow.child || '', String(flow.seq || 0)].join('~');
}
export function parseFlow(text) {
  const [id, parent, child, seq] = String(text || '').split('~');
  if (!/^f_[0-9a-f]{8,32}$/.test(id || '')) return null;
  const clean = value => (/^[pc]_[0-9a-f]{8,32}$/.test(value || '') ? value : null);
  return { id, parent: clean(parent), child: clean(child), seq: Number.isFinite(Number(seq)) ? Number(seq) : 0 };
}

let flow = null;
function current() {
  if (flow) return flow;
  let fromLink = null;
  let saved = null;
  try {
    fromLink = parseFlow(new URLSearchParams(window.location.search).get('flow'));
  } catch (_) {
    fromLink = null;
  }
  try {
    saved = parseFlow(window.localStorage.getItem(FLOW_KEY));
  } catch (_) {
    saved = null;
  }
  flow = fromLink || saved || fresh();
  keep();
  return flow;
}
function keep() {
  try {
    window.localStorage.setItem(FLOW_KEY, formatFlow(flow));
  } catch (_) {
    // private windows: the visit still counts within this page
  }
}

/** The visit, for the link back to the portal. */
export const flowParam = () => formatFlow(current());

/** Records one completed fact. Never throws into the product: a bad event is dropped with a warning. */
export function track(event, props = {}) {
  if (!PLAYER_EVENTS.has(event)) {
    console.warn(`[analytics] ${event} is not in the tracking plan`);
    return null;
  }
  const bad = Object.keys(props).find(key => FORBIDDEN.test(key));
  if (bad) {
    console.warn(`[analytics] ${event} dropped: forbidden property ${bad}`);
    return null;
  }
  const visit = current();
  visit.seq += 1;
  keep();
  const properties = {
    token: MIXPANEL_TOKEN,
    distinct_id: visit.id,
    time: Date.now(),
    $insert_id: uuid(),
    flow_session_id: visit.id,
    flow_seq: visit.seq,
    side: 'C',
    surface: 'player',
    env: MIXPANEL_ENV,
    qa_run: typeof navigator !== 'undefined' && navigator.webdriver === true,
    ...(visit.parent ? { parent_id: visit.parent } : {}),
    ...(visit.child ? { child_id: visit.child } : {}),
    ...props,
  };
  if (ON && typeof fetch === 'function') {
    fetch('https://api-js.mixpanel.com/track?ip=0', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `data=${encodeURIComponent(JSON.stringify([{ event, properties }]))}`,
      keepalive: true,
    }).catch(() => {});
  }
  return { event, properties, sent: ON };
}
